import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../AuthContext';
import { API_BASE } from '../../apiConfig';
import { Button, Panel, Segmented, SelectField, Slider } from '../ui';

/**
 * Live game settings — pushes a parameter to the running game with session:param.
 * Definitions come from GET /api/settings/{game} (marked deprecated server-side, but it is still
 * the only endpoint that returns type/min/max/strings); current values from
 * GET /api/settings?game_id&phase&user_id (providers must pass the patient's user_id).
 * Live changes are not saved to the patient's settings — Game Parameters on the patient page does that.
 */
const PHASES = [{ value: 'warmup', label: 'Warmup' }, { value: 'condition', label: 'Condition' }, { value: 'cooldown', label: 'Cooldown' }];

const typeOf = (p) => p.$type || p.type || p.parm_type || '';
const asBool = (v) => v === true || v === 'true' || v === 1 || v === '1';

export default function GameSettingsCard({ game, userId, isProvider, send }) {
    const { authFetch } = useAuth();
    const [phase, setPhase] = useState('warmup');
    const [defs, setDefs] = useState(null);
    const [values, setValues] = useState({});
    const [state, setState] = useState('idle'); // idle | loading | error | empty | ready
    const [error, setError] = useState(null);
    const [pushed, setPushed] = useState({}); // key -> time sent
    const timers = useRef({});

    const load = useCallback(async () => {
        if (!game) return;
        setState('loading');
        setError(null);
        try {
            const id = game.toLowerCase();
            // Deprecated on the server ("use api/settings/all"), but /all returns values only.
            const defRes = await authFetch(`${API_BASE}/api/settings/${encodeURIComponent(id)}`);
            if (defRes.status === 404) { setDefs({}); setState('empty'); return; }
            if (!defRes.ok) throw new Error(`Parameter definitions didn’t load (${defRes.status})`);
            const defJson = await defRes.json();
            const d = defJson.data || defJson;
            const qs = new URLSearchParams({ game_id: id, phase });
            if (userId) qs.set('user_id', userId);
            const vals = {};
            Object.entries(d).forEach(([k, p]) => { if (p && typeof p === 'object') vals[k] = p.value_ ?? p.value ?? p.Value ?? ''; });
            if (isProvider ? !!userId : true) {
                const res = await authFetch(`${API_BASE}/api/settings?${qs.toString()}`);
                if (res.ok) {
                    const j = await res.json();
                    Object.entries(j.data || {}).forEach(([k, v]) => { if (k in vals) vals[k] = v; });
                }
            }
            setDefs(d);
            setValues(vals);
            setState(Object.keys(d).length ? 'ready' : 'empty');
        } catch (e) {
            setError(e.message);
            setState('error');
        }
    }, [authFetch, game, phase, userId, isProvider]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

    const push = (key, def, value) => {
        setValues((v) => ({ ...v, [key]: value }));
        if (!send(def.name || key, value, phase)) return;
        setPushed((p) => ({ ...p, [key]: Date.now() }));
        clearTimeout(timers.current[key]);
        timers.current[key] = setTimeout(() => setPushed((p) => { const n = { ...p }; delete n[key]; return n; }), 5000);
    };

    const rows = defs ? Object.entries(defs).filter(([, p]) => p && typeof p === 'object') : [];

    return (
        <Panel title="Game settings" note={game || undefined}>
            <div className="ls-rows">
                <div className="ls-set-phase">
                    <Segmented size="sm" label="Phase" options={PHASES} value={phase} onChange={setPhase} />
                </div>
                {state === 'loading' ? <p className="eg-small eg-secondary">Loading parameters…</p> : null}
                {state === 'error' ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '8px 0 16px' }}>
                        <span className="eg-small" style={{ color: 'var(--egdl-danger-ink)', fontWeight: 600 }}>{error}</span>
                        <Button variant="secondary" onClick={load}>Retry</Button>
                    </div>
                ) : null}
                {state === 'empty' ? <p className="eg-small eg-secondary">No live parameters</p> : null}
                {!game ? <p className="eg-small eg-secondary">Waiting for the game…</p> : null}
                {state === 'ready' ? rows.map(([key, p]) => {
                    const t = typeOf(p);
                    const label = p.alias || p.name || key;
                    const on = !!pushed[key];
                    const v = values[key];
                    let control = null;
                    let display = String(v ?? '');
                    if (t.includes('Bool')) {
                        display = asBool(v) ? 'On' : 'Off';
                        control = <Segmented label={label} options={[{ value: true, label: 'On' }, { value: false, label: 'Off' }]} value={asBool(v)} onChange={(nv) => push(key, p, nv)} />;
                    } else if (t.includes('Range')) {
                        const min = Number(p.min_ ?? p.min ?? 0);
                        const max = Number(p.max_ ?? p.max ?? 1);
                        const step = Number(p.tick_ ?? p.tick ?? 0.01) || 0.01;
                        const num = Number.isFinite(Number(v)) ? Number(v) : min;
                        const dp = step >= 1 ? 0 : Math.min(3, Math.ceil(-Math.log10(step)));
                        display = num.toFixed(dp);
                        control = (
                            <Slider label={label} value={num} min={min} max={max} step={step} rail={on ? 'var(--egdl-accent-rail)' : undefined}
                                onChange={(nv) => setValues((vals) => ({ ...vals, [key]: nv }))} onCommit={(nv) => push(key, p, nv)} />
                        );
                    } else if (t.includes('StringList')) {
                        const opts = (p.strings || p.Strings || []).map((o) => ({ value: o, label: o }));
                        control = opts.length ? <SelectField ariaLabel={label} value={v ?? ''} options={opts} onChange={(nv) => push(key, p, nv)} /> : null;
                        display = '';
                    }
                    return (
                        <div key={key} className={'ls-set' + (on ? ' ls-set--pushed' : '')} title={p.description || undefined}>
                            <div className="ls-set__top">
                                <span className="ls-set__label">{label}</span>
                                {display ? <span className="ls-set__value">{display}</span> : null}
                            </div>
                            {control}
                            {on ? <span className="ls-caption ls-caption--accent" role="status">Sent to the game · {new Date(pushed[key]).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span> : null}
                        </div>
                    );
                }) : null}
            </div>
        </Panel>
    );
}
